<template>
  <div
    v-if="index === 3 && totalProduct > 4"
    class="product-card product-card--all"
  >
    <router-link
      :to="product.brand.link"
      class="view-all__link"
      @click="trackBrandHighlightedClick(product.brand, carouselUuid)"
    >
      <img
        :src="getImageSquared"
        alt="background"
        class="view-all__background"
      />
      <div class="view-all__overlay">
        <div class="view-all__count"> +{{ totalProduct }} {{ t('discovery.brandSneakPeek.tile.nbProducts') }} </div>
        <AkButton
          size="small"
          link
        >
          {{ t('discovery.brandSneakPeek.view all') }}
        </AkButton>
      </div>
    </router-link>
  </div>
  <div
    v-else-if="product"
    class="product-card"
  >
    <router-link
      :to="product.link"
      class="product-card__image-link"
      @click="trackProductClick({ product: product, index: index }, carouselUuid)"
    >
      <div
        class="product-card__image-wrapper"
        @mouseenter="isHovered = true"
        @mouseleave="isHovered = false"
      >
        <img
          class="product-card__image"
          :src="getImageSquared"
          alt="title"
        />
        <div
          class="product-card__price"
          :class="{ 'product-card__price--discount': product.discount_rate !== 0 }"
        >
          {{ formatPrice({ amount: product.wholesale_price.amount, currency: $currency }) }}
        </div>
        <div class="product-card__title">
          {{ product.name }}
        </div>
      </div>
    </router-link>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { SneakPeekProduct } from '@client/modules/discovery/feature/brands-sneak-peek/types/types';
import { useTracking } from '@bc/discovery/feature/brands-sneak-peek/composables/use-tracking';
import { imgCdnUrlFill } from '@bc/shared';
import usePrice from '@core/composables/use-price';
import useCurrency from '@client/monolith/core/composables/use-currency';
import { useI18n } from 'vue-i18n';

const { formatPrice } = usePrice();
const { t } = useI18n();
const { trackBrandHighlightedClick, trackProductClick } = useTracking();
const { $currency } = useCurrency();

const props = defineProps<{
  product: SneakPeekProduct | null;
  totalProduct?: number;
  index?: number;
  carouselUuid?: string;
}>();

const isHovered = ref(false);

const getImageSquared = computed(() => {
  return isHovered.value && props.product?.images[1]
    ? imgCdnUrlFill(props.product.images[1], '300')
    : imgCdnUrlFill(props.product.images[0], '300');
});
</script>
<style scoped lang="scss">
.product-card {
  @apply ds-rounded-md ds-overflow-hidden ds-bg-white ds-shadow-md ds-transition-all ds-duration-300 ds-cursor-pointer;
  margin: 0 auto;
}
.product-card:hover {
  @apply ds-shadow-sm;
}
.product-card__image-wrapper {
  @apply ds-relative ds-h-full;
}
.product-card__image-wrapper::before {
  content: '';
  @apply ds-absolute ds-inset-0 ds-bg-primary ds-opacity-0 ds-transition-opacity ds-duration-300 ds-z-20;
}
.product-card__image-wrapper:hover::before {
  @apply ds-opacity-20;
}
.product-card__image-wrapper:hover .product-card__image {
  @apply ds-transition-all ds-duration-300;
}
.product-card__image {
  @apply ds-h-full ds-w-auto ds-block ds-object-cover;
}
.product-card__price {
  @apply ds-absolute ds-top-2 ds-left-2 ds-bg-white ds-text-primary ds-px-2.5 ds-py-1.5 ds-rounded-sm ds-font-bold ds-text-sm ds-z-20;
}
.product-card__price--discount {
  @apply ds-text-discount-700;
}
.product-card__title {
  @apply ds-absolute ds-bottom-2 ds-left-2 ds-right-2 ds-bg-white ds-text-primary ds-font-bold ds-text-center ds-text-sm ds-opacity-0 ds-translate-y-5 ds-pointer-events-none ds-z-20;
  @apply ds-transition-opacity ds-transition-transform ds-duration-300 ds-p-2 ds-rounded-sm;
}
.product-card:hover .product-card__title {
  @apply ds-opacity-100 ds-translate-y-0 ds-pointer-events-auto;
}
.product-card--all {
  @apply ds-relative ds-h-full ds-w-full ds-rounded-md ds-overflow-hidden ds-flex ds-items-center ds-justify-center ds-shadow-md ds-transition-all ds-duration-300;

  .view-all__background {
    @apply ds-absolute ds-inset-0 ds-w-full ds-h-full ds-object-cover;
    filter: blur(4px) brightness(1.2);
    z-index: 0;
  }

  .view-all__overlay {
    @apply ds-absolute ds-inset-0 ds-flex ds-flex-col ds-items-center ds-justify-center ds-text-center ds-transition-all ds-duration-300;
    background-color: rgba(251, 248, 243, 0.8);
    z-index: 1;
  }

  .view-all__count {
    @apply ds-text-sm ds-font-bold;
  }

  .view-all__link {
    @apply ds-underline ds-text-sm ds-font-medium ds-mt-2;
  }
}
.product-card--all:hover {
  @apply ds-shadow-sm;

  .view-all__overlay {
    background-color: rgba(251, 248, 243, 0.9);
  }
}
</style>
